// Number-crunching behind the admin Dashboard. Pure: takes raw rows (see fetchInsightsData in
// admin.js) and returns what the view draws, so it can be tested with plain objects.
//
// Revenue = payment_status 'paid' and not cancelled. Windows are IST calendar days;
// "today" (days = 0) is bucketed by hour and compared with yesterday up to the same time.
// Every window is compared with the equal-length window just before it.

const HOUR = 3600e3, DAY = 86400e3, IST = 330 * 60e3; // India has no DST

const ist = d => new Date(new Date(d).getTime() + IST); // read with getUTC*() for IST wall-clock
export const istDayStart = d => {
  const s = ist(d);
  return Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate()) - IST;
};
export const istHour = d => ist(d).getUTCHours();

/** Time bounds (ms) for a range: `days` is 0 (today), 7, 30 or 90. `n` buckets of `step` ms each. */
export function insightsWindow(days, now = new Date()) {
  const today = days === 0;
  const since = today ? istDayStart(now) : istDayStart(now) - (days - 1) * DAY;
  return {
    today,
    since,
    prevSince: since - (today ? DAY : days * DAY),
    prevUntil: today ? now.getTime() - DAY : since,
    step: today ? HOUR : DAY,
    n: today ? istHour(now) + 1 : days,
  };
}

/** Change against a previous figure. null when there is nothing to compare with. */
export function change(now, prev) {
  if (!prev) return null;
  const pct = ((now - prev) / prev) * 100;
  return { pct, dir: Math.abs(pct) < 0.05 ? 'flat' : pct > 0 ? 'up' : 'down' };
}

const tally = (list, key) => {
  const m = new Map();
  for (const x of list) m.set(key(x), (m.get(key(x)) || 0) + 1);
  return [...m].sort((a, b) => b[1] - a[1]);
};

/**
 * @param {{orders, items, profiles, customerCount, categories, products, lowStock, outOfStock, counts}} raw
 * @param {number} days 0 | 7 | 30 | 90
 */
export function buildInsights(raw, days = 30, now = new Date()) {
  const w = insightsWindow(days, now);
  const ts = o => new Date(o.created_at).getTime();
  const isRev = o => o.payment_status === 'paid' && o.status !== 'cancelled';
  const orders = raw.orders || [];
  const curOrders = orders.filter(o => ts(o) >= w.since);
  const prevOrders = orders.filter(o => ts(o) >= w.prevSince && ts(o) < w.prevUntil);

  const side = (list, from, profileLo, profileHi) => {
    const series = Array.from({ length: w.n }, (_, i) => ({ t: from + i * w.step, revenue: 0, orders: 0 }));
    for (const o of list) {
      if (!isRev(o)) continue;
      const b = series[Math.floor((ts(o) - from) / w.step)];
      if (b) { b.revenue += Number(o.total); b.orders += 1; }
    }
    const revenue = series.reduce((s, b) => s + b.revenue, 0);
    const count = series.reduce((s, b) => s + b.orders, 0);
    const newCustomers = (raw.profiles || []).filter(p => {
      const t = new Date(p.created_at).getTime();
      return t >= profileLo && t < profileHi;
    }).length;
    return { series, revenue, orders: count, aov: count ? revenue / count : 0, newCustomers };
  };
  const cur = side(curOrders, w.since, w.since, Infinity);
  const prev = side(prevOrders, w.prevSince, w.prevSince, w.prevUntil);

  const paid = curOrders.filter(isRev);
  const perBuyer = new Map();
  for (const o of paid) if (o.user_id) perBuyer.set(o.user_id, (perBuyer.get(o.user_id) || 0) + 1);
  const refunded = curOrders.filter(o => o.payment_status === 'refunded');

  // Top products / categories from the lines of paid orders in the window.
  const catOf = new Map((raw.products || []).map(p => [p.id, p.category_id]));
  const catName = new Map((raw.categories || []).map(c => [c.id, c.name]));
  const byProduct = new Map();
  const byCategory = new Map();
  let itemsSold = 0;
  for (const i of raw.items || []) {
    const qty = Number(i.quantity) || 0, line = Number(i.line_total) || 0;
    itemsSold += qty;
    const key = i.product_id ?? i.product_name;
    const p = byProduct.get(key) || { id: i.product_id ?? null, name: i.product_name || `Product #${i.product_id}`, units: 0, revenue: 0 };
    p.units += qty; p.revenue += line;
    byProduct.set(key, p);
    const cat = catName.get(catOf.get(i.product_id)) || 'Uncategorised';
    byCategory.set(cat, (byCategory.get(cat) || 0) + line);
  }
  const byRevenue = (a, b) => b.revenue - a.revenue;

  const states = new Map();
  for (const o of paid) {
    const k = (o.state || '').trim() || 'Unknown';
    const s = states.get(k) || { name: k, orders: 0, revenue: 0 };
    s.orders += 1; s.revenue += Number(o.total);
    states.set(k, s);
  }

  const byHour = Array(24).fill(0);
  for (const o of curOrders) if (o.payment_status === 'paid' || o.payment_status === 'refunded') byHour[istHour(o.created_at)] += 1;

  const stock = [...(raw.outOfStock || []).map(p => ({ ...p, stock_qty: 0 })), ...(raw.lowStock || [])]
    .sort((a, b) => a.stock_qty - b.stock_qty || String(a.name).localeCompare(String(b.name)));

  return {
    days, today: w.today, since: w.since, until: now.getTime(),
    cur, prev, itemsSold,
    customers: raw.customerCount ?? 0,
    buyers: perBuyer.size,
    repeatBuyers: [...perBuyer.values()].filter(n => n > 1).length,
    refunds: { orders: refunded.length, amount: refunded.reduce((s, o) => s + Number(o.total), 0) },
    pipeline: tally(curOrders.filter(o => o.payment_status === 'paid'), o => o.status).map(([status, n]) => ({ status, orders: n })),
    payments: tally(curOrders, o => o.payment_status).map(([status, n]) => ({ status, orders: n })),
    topProducts: [...byProduct.values()].sort(byRevenue).slice(0, 8),
    topCategories: [...byCategory].map(([name, revenue]) => ({ name, revenue })).sort(byRevenue).slice(0, 6),
    topStates: [...states.values()].sort(byRevenue).slice(0, 8),
    lowStock: stock.slice(0, 6),
    stockCount: stock.length,
    byHour,
    recentOrders: raw.recentOrders || [],
    attention: {
      toShip: raw.counts?.toShip ?? 0,
      messages: raw.counts?.messages ?? 0,
      reviews: raw.counts?.reviews ?? 0,
      stock: stock.length,
    },
  };
}
